import React, { useRef, useState, useEffect } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { withTranslation } from "react-i18next";

import {
  Button,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  MenuItem,
  IconButton,
  Box,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import useStyles from "./styles";
import useGlobalStyles from "../../utils/globalStyles";

import {
  getalllocalstore,
  AddNewCustomerType,
  UpdateCustomerTypeCharges,
} from "../../apollo";

const GET_LOCAL_STORE = gql`${getalllocalstore}`;
const CREATE_CUSTOMER_TYPE = gql`${AddNewCustomerType}`;
const UPDATE_CUSTOMER_TYPE_CHARGES = gql`${UpdateCustomerTypeCharges}`;

function UpdateCustomerType({ vendor, onClose, refetch, t }) {
  const formRef = useRef();
  const classes = useStyles();
  const globalClasses = useGlobalStyles();

  const isEditMode = !!vendor;

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState(false);
  const inputTextStyle = { color: 'black' };

  const { data: storeData } = useQuery(GET_LOCAL_STORE);

  const activeStores =
    storeData &&
    storeData.getAllLocalStores &&
    storeData.getAllLocalStores.filter((s) => s.isActive);

  const [selectedStoreId, setSelectedStoreId] = useState(
    vendor &&
      vendor.storeCharges &&
      vendor.storeCharges[0] &&
      vendor.storeCharges[0].localStoreId
      ? vendor.storeCharges[0].localStoreId
      : ""
  );

  const [formData, setFormData] = useState({
    name: vendor && vendor.name ? vendor.name : "",
    description: vendor && vendor.description ? vendor.description : "",
    customerType: vendor && vendor.customerType ? vendor.customerType : "",
    charges: {
      handling: [{ minAmount: "", maxAmount: "", rate: "" }],
      delivery: [{ minAmount: "", maxAmount: "", rate: "" }],
    },
  });

  useEffect(() => {
    if (!isEditMode || !vendor || !selectedStoreId) return;

    const storeCharge =
      vendor.storeCharges &&
      vendor.storeCharges.find((c) => c.localStoreId === selectedStoreId);

    setFormData((p) => ({
      ...p,
      charges: {
        handling:
          storeCharge &&
          storeCharge.handling &&
          storeCharge.handling.length > 0
            ? storeCharge.handling
            : [{ minAmount: "", maxAmount: "", rate: "" }],

        delivery:
          storeCharge &&
          storeCharge.delivery &&
          storeCharge.delivery.length > 0
            ? storeCharge.delivery
            : [{ minAmount: "", maxAmount: "", rate: "" }],
      },
    }));
  }, [vendor, selectedStoreId, isEditMode]);

  const handleChargeChange = (type, index, field, value) => {
    const updated = formData.charges[type].map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );

    setFormData((p) => ({
      ...p,
      charges: {
        ...p.charges,
        [type]: updated,
      },
    }));
  };

  const addChargeRow = (type) => {
    setFormData((p) => ({
      ...p,
      charges: {
        ...p.charges,
        [type]: [...p.charges[type], { minAmount: "", maxAmount: "", rate: "" }],
      },
    }));
  };

  const deleteChargeRow = (type, index) => {
    let updated = formData.charges[type].filter((_, i) => i !== index);

    if (updated.length === 0) {
      updated = [{ minAmount: "", maxAmount: "", rate: "" }];
    }

    setFormData((p) => ({
      ...p,
      charges: {
        ...p.charges,
        [type]: updated,
      },
    }));
  };

  const mutation = isEditMode
    ? UPDATE_CUSTOMER_TYPE_CHARGES
    : CREATE_CUSTOMER_TYPE;

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: () => {
      setSuccess(
        isEditMode
          ? t("Charges Updated Successfully")
          : t("Customer Type Added Successfully")
      );
      setOpenSnack(true);

      if (refetch) refetch();

      setTimeout(() => {
        setOpenSnack(false);
        onClose();
      }, 3000);
    },
    onError: () => {
      setError("Something went wrong!");
      setOpenSnack(true);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEditMode) {
      mutate({
        variables: {
          type: Number(formData.customerType),
          localStoreId: selectedStoreId,
          charges: {
            handling: formData.charges.handling.map((h) => ({
              minAmount: Number(h.minAmount),
              maxAmount: Number(h.maxAmount),
              rate: Number(h.rate),
            })),
            delivery: formData.charges.delivery.map((d) => ({
              minAmount: Number(d.minAmount),
              maxAmount: Number(d.maxAmount),
              rate: Number(d.rate),
            })),
          },
        },
      });
    } else {
      mutate({
        variables: {
          name: formData.name,
          description: formData.description,
          customerType: Number(formData.customerType),
        },
      });
    }
  };

  return (
    <Dialog open maxWidth="md" fullWidth onClose={onClose}>
      <form ref={formRef} onSubmit={handleSubmit}>
        <DialogTitle>
          <Box className={classes.heading}>
            <Typography className={classes.textWhite}>
              {isEditMode
                ? t("Edit Customer Type Charges")
                : t("Add Customer Type")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {isEditMode && (
              <Grid item xs={12}>
               <TextField
              select
              label="Store"
              fullWidth
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              InputLabelProps={{ style: { color: "black" } }}
              sx={{
                "& .MuiSelect-select": { color: "black" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
              }}
            >
              {activeStores &&
                activeStores.map((store) => (
                  <MenuItem
                    key={store._id}
                    value={store._id}
                    sx={{ color: "black" }}
                  >
                    {store.name}
                  </MenuItem>
                ))}
            </TextField>
              </Grid>
            )}

            {["handling", "delivery"].map((type) => (
              <React.Fragment key={type}>
                <Grid item xs={12}>
                  <Typography fontWeight="bold" sx={{ color: "black" }}>
                    {type} Charges
                  </Typography>
                </Grid>

                {formData.charges[type].map((row, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Min Amount"
                        type="number"
                        value={row.minAmount}
                        onChange={(e) =>
                          handleChargeChange(
                            type,
                            index,
                            "minAmount",
                            e.target.value
                          )
                        }
                        inputProps={{ style: inputTextStyle }}
InputLabelProps={{ style: inputTextStyle }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Max Amount"
                        type="number"
                        value={row.maxAmount}
                        onChange={(e) =>
                          handleChargeChange(
                            type,
                            index,
                            "maxAmount",
                            e.target.value
                          )
                        }
                        inputProps={{ style: inputTextStyle }}
InputLabelProps={{ style: inputTextStyle }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Rate"
                        type="number"
                        value={row.rate}
                        onChange={(e) =>
                          handleChargeChange(
                            type,
                            index,
                            "rate",
                            e.target.value
                          )
                        }
                        inputProps={{ style: inputTextStyle }}
InputLabelProps={{ style: inputTextStyle }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={3}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <IconButton
                        color="error"
                        onClick={() => deleteChargeRow(type, index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addChargeRow(type)}
                  >
                    + Add {type}
                  </Button>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="submit"
            className={globalClasses.dashbordBtn}
            disabled={loading}
          >
            {t("Update")}
          </Button>
          <Button
            className={globalClasses.modalCancleBtn}
            onClick={onClose}
          >
            {t("Cancel")}
          </Button>
        </DialogActions>
      </form>

      <Snackbar
  open={openSnack}
  autoHideDuration={4000}
  onClose={() => setOpenSnack(false)}
>
  <Alert
    severity={success ? "success" : "error"}
    variant="filled"
  >
    {success || error}
  </Alert>
</Snackbar>
    </Dialog>
  );
}

export default withTranslation()(UpdateCustomerType);
